import { Box, Typography } from "@mui/material";
import { SettingsFlow } from "@ory/kratos-client";
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Configuration, isAccountSettingsVisible } from "../../../../config";
import { api, getRedirectBrowserTo, isFlowInstance } from "../../api";
import { UiForm, UiMessages } from "../../components";
import { FullPageLoader } from "../../layout";
import { ChangeLanguageForm } from "./ChangeLanguageForm";

function isNotEmpty(str: string | null | undefined): str is string {
  return !!str && str.length > 0;
}

export const AccountSettingsPage: React.FC = () => {
  if (!isAccountSettingsVisible()) {
    return <Navigate to="/settings/notifications" />;
  }
  return <AccountSettingsPageComponent />;
};

const AccountSettingsPageComponent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flow, setFlow] = useState<SettingsFlow | null>(null);
  const [loading, setLoading] = useState(false);
  const flowId = searchParams.get("flow");

  const manageExternalLogin = Configuration.settings.includes("oidc");
  const changePassword = Configuration.settings.includes("password");
  const manageMfa = Configuration.settings.includes("mfa");
  const manageLanguage = Configuration.settings.includes("language");

  useEffect(() => {
    if (loading || flow) {
      return;
    }

    function initiateSettingsFlow(): Promise<SettingsFlow> {
      return api.createBrowserSettingsFlow().then((response) => response.data);
    }

    setLoading(true);
    let promise: Promise<SettingsFlow>;
    if (isNotEmpty(flowId)) {
      promise = api
        .getSettingsFlow({ id: flowId })
        .then((response) => response.data, initiateSettingsFlow);
    } else {
      promise = initiateSettingsFlow();
    }
    promise
      .then((loadedFlow) => {
        setFlow(loadedFlow);
        setLoading(false);
        navigate(`/settings/account?flow=${loadedFlow.id}`);
      })
      .catch((err) => {
        // TODO: show error
        setLoading(false);
      });
  }, [loading, setLoading, flowId, flow, setFlow, navigate]);

  function handleSubmit(values: any): Promise<void> {
    return api
      .updateSettingsFlow({ flow: flow!.id, updateSettingsFlowBody: values })
      .then((response) => {
        setFlow(response.data);
      })
      .catch((err) => {
        const response = err.response?.data;
        const redirectUrl = getRedirectBrowserTo(err?.response.data);
        if (redirectUrl) {
          document.location = redirectUrl;
        } else if (isFlowInstance<SettingsFlow>(response)) {
          setFlow(response);
        }
      });
  }

  if (loading || !flow) {
    return <FullPageLoader />;
  }
  return (
    <Box marginTop={2}>
      <Box marginBottom={2}>
        {flow.ui.messages && <UiMessages messages={flow.ui.messages} />}
      </Box>
      {changePassword && (
        <UiForm handleSubmit={handleSubmit} ui={flow.ui} groups={["password"]}>
          <Typography variant="h6">Promeni lozinku</Typography>
        </UiForm>
      )}
      {manageExternalLogin && (
        <UiForm handleSubmit={handleSubmit} ui={flow.ui} groups={["oidc"]}>
          <Typography variant="h6">External login</Typography>
        </UiForm>
      )}

      {manageMfa && (
        <>
          <UiForm handleSubmit={handleSubmit} ui={flow.ui} groups={["totp"]}>
            <Typography variant="h6">Multi-Factor Authentication</Typography>
            <Typography>
              Add a TOTP Authenticator App to your account to improve your
              account security. Popular Authenticator Apps are{" "}
              <a
                href="https://www.lastpass.com"
                target="_blank"
                rel="noreferrer"
              >
                LastPass
              </a>{" "}
              and Google Authenticator (
              <a
                href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                target="_blank"
                rel="noreferrer"
              >
                iOS
              </a>
              ,{" "}
              <a
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en&gl=US"
                target="_blank"
                rel="noreferrer"
              >
                Android
              </a>
              ).
            </Typography>
          </UiForm>
          <UiForm
            handleSubmit={handleSubmit}
            ui={flow.ui}
            groups={["lookup_secret"]}
          >
            <Typography variant="h6">
              Manage 2FA Backup Recovery Codes
            </Typography>

            <Typography>
              Recovery codes can be used in panic situations where you have lost
              access to your 2FA device.
            </Typography>
          </UiForm>
        </>
      )}
      {manageLanguage && <ChangeLanguageForm />}
    </Box>
  );
};
